import { useBasketsStore, type addItemOpts } from '~/stores/basketsStore'
import { useWishlistStore } from '~/stores/wishlistStore'

export type GAOpts = {
  product: any
  location: string
  group?: string
  locale?: Locales
}

type RemoveItemOpts = {
  itemId: string
  basketId: string
}

export const useCartActions = () => {
  const basketsStore = useBasketsStore()
  const wishlistStore = useWishlistStore()
  const { removeItemFromBasket } = useBasket()
  const { trackConversion } = useConstructorTracker()

  async function addToBagAction(productOpts: addItemOpts, gaOpts: GAOpts) {
    await basketsStore.addItem(productOpts)
    // REVIEW: Should we track the add-to-bag error in GA as well?
    if (basketsStore.error) return

    trackAddToBag({
      cartOpts: {
        product: gaOpts.product,
        quantity: 1,
        currentCart: basketsStore.basket,
      },
      gaOpts: {
        group: gaOpts.group ?? 'cart',
        location: gaOpts.location,
      },
      locale: gaOpts.locale ?? useNuxtApp().$i18n.locale.value,
      wishlist: wishlistStore.wishlist,
    })

    trackConversion(gaOpts.product)
  }

  async function removeFromBagAction(productOpts: RemoveItemOpts, gaOpts: GAOpts) {
    const updatedCart = await removeItemFromBasket(productOpts.itemId, productOpts.basketId)
    if (updatedCart instanceof Error) throw updatedCart
    await basketsStore.setBasket(updatedCart)

    useBrazeTracking().logCustomEvent('remove_from_bag', {
      // Track product removed
      product_id: gaOpts.product.productId,
      product_name: gaOpts.product.productName,
      product_price: gaOpts.product.price,
      product_image_url: gaOpts.product.image?.url,
      product_primary_category: gaOpts.product?.productCustomAttributes?.c_mHJPrimaryCategory,

      // Add full basket context
      basket_id: updatedCart.basketId,
      basket_total: updatedCart.orderTotal,
      basket_currency: updatedCart.currency,
      products_in_basket: updatedCart.productItems, // NOTE: Braze Phase 1 has approved sending all line item data as is
    })

    trackRemoveFromBag({
      cartOpts: {
        product: gaOpts.product,
        quantity: 1,
        currentCart: basketsStore.basket,
      },
      gaOpts: {
        group: gaOpts.group ?? 'cart',
        location: gaOpts.location,
      },
      locale: gaOpts.locale ?? useNuxtApp().$i18n.locale.value,
      wishlist: wishlistStore.wishlist,
    })
  }

  async function updateItemBagAction(productOpts: UpdateItemOpts, gaOpts: GAOpts) {
    const prevBasketState: BasketWithProductData = JSON.parse(JSON.stringify(basketsStore.basket))
    const updatedCart = await basketsStore.updateItem(productOpts)

    if (updatedCart instanceof Error || !updatedCart)
      throw new Error(updatedCart?.message ?? 'failed to update item in basket')
    await basketsStore.setBasket(updatedCart)

    const productPrevState = prevBasketState.productItems?.find((item) => item.itemId === productOpts.itemId)
    if (!productPrevState) {
      // this should not be possible, but just to satisfy ts just return and not track events in this case
      return
    }
    prevBasketState.productItems = prevBasketState.productItems?.filter((item) => item.itemId !== productOpts.itemId)

    trackRemoveFromBag({
      cartOpts: {
        product: productPrevState,
        quantity: 1,
        currentCart: prevBasketState,
      },
      gaOpts: {
        group: gaOpts.group ?? 'cart',
        location: gaOpts.location,
      },
      locale: gaOpts.locale ?? useNuxtApp().$i18n.locale.value,
      wishlist: wishlistStore.wishlist,
    })
    trackAddToBag({
      cartOpts: {
        product: gaOpts.product,
        quantity: 1,
        currentCart: basketsStore.basket,
        customQuery: {
          c_engravingText: productOpts?.engravingText,
          c_ringSize: productOpts?.size,
          optionItems: productOpts?.optionItems,
        },
      },
      gaOpts: {
        group: gaOpts.group ?? 'cart',
        location: gaOpts.location,
      },
      locale: gaOpts.locale ?? useNuxtApp().$i18n.locale.value,
      wishlist: wishlistStore.wishlist,
    })
    trackConversion(gaOpts.product)
  }

  return {
    addToBagAction,
    removeFromBagAction,
    updateItemBagAction,
  }
}
